import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, Barbell, Star, Spinner, House, PersonSimpleRun, Lightning, BookOpen, ShareNetwork } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EQUIP_FILTERS = [
  { id: null, label: "All", icon: null },
  { id: "Gym", label: "Gym", icon: Barbell },
  { id: "Home", label: "Home", icon: House },
  { id: "Bodyweight", label: "Bodyweight", icon: PersonSimpleRun },
];

export default function Templates({ templates, api, onRefresh, profile, categoryFilter, onOpenTemplate, onQuickLog, onNavigate }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Custom");
  const [equipment, setEquipment] = useState("Gym");
  const [description, setDescription] = useState("");
  const [exercises, setExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [loadingRec, setLoadingRec] = useState(false);
  const [recError, setRecError] = useState(false);
  const [recFetched, setRecFetched] = useState(false);
  const [equipFilter, setEquipFilter] = useState(null);
  const [lastPerformed, setLastPerformed] = useState({}); // {tpl_name_lower: ISO_date}

  useEffect(() => {
    if (profile && !recFetched) fetchRecommended();
  }, [profile, recFetched]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${api}/templates/last-performed`);
        if (!res.ok) return;
        const data = await res.json();
        setLastPerformed(data || {});
      } catch (e) { /* ignore */ }
    })();
  }, [api, templates]);

  const fetchRecommended = async () => {
    setLoadingRec(true);
    setRecError(false);
    setRecFetched(true);
    try {
      const res = await axios.post(`${api}/templates/recommended`);
      setRecommended(res.data.templates || []);
    } catch (e) {
      console.error("Recommended templates error:", e);
      setRecError(true);
      setRecFetched(false);
    } finally {
      setLoadingRec(false);
    }
  };

  const addExercise = () => {
    setExercises([...exercises, { name: "", numSets: "", reps: "", weight: "", notes: "" }]);
  };

  const removeExercise = (idx) => setExercises(exercises.filter((_, i) => i !== idx));

  const updateExerciseField = (idx, field, value) => {
    const updated = [...exercises];
    updated[idx] = { ...updated[idx], [field]: value };
    setExercises(updated);
  };

  const saveTemplate = async () => {
    if (!name.trim()) { toast.error("Workout name required"); return; }
    setSaving(true);
    try {
      // Build exercises payload: expand numSets/reps/weight into a sets array (optional)
      const payloadExercises = exercises
        .filter((ex) => ex.name.trim())
        .map((ex) => {
          const n = Math.max(parseInt(ex.numSets) || 0, 0);
          const reps = parseInt(ex.reps) || 0;
          const weight = parseFloat(ex.weight) || 0;
          const sets = n > 0
            ? Array.from({ length: n }, () => ({ reps, weight, completed: false }))
            : (reps > 0 || weight > 0 ? [{ reps, weight, completed: false }] : []);
          return { name: ex.name.trim(), sets, notes: ex.notes || "" };
        });
      await axios.post(`${api}/templates`, { name, category, equipment, description, exercises: payloadExercises });
      toast.success("Workout created!");
      setShowForm(false);
      setName(""); setCategory("Custom"); setEquipment("Gym"); setDescription(""); setExercises([]);
      onRefresh();
    } catch (e) {
      toast.error("Failed to create workout");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${api}/templates/${id}`);
      toast.success("Template deleted");
      onRefresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const shareTemplate = async (template) => {
    // Build the public share URL — use window.location.origin so the URL is
    // correct in any environment (preview, prod, staging).
    const url = `${window.location.origin}/t/${template.id}`;
    const shareText = `Check out my "${template.name}" workout on UpTop`;
    // Prefer native share sheet on mobile; fall back to clipboard.
    if (navigator.share) {
      try {
        await navigator.share({ title: template.name, text: shareText, url });
        return;
      } catch (e) {
        // User cancelled — silently fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere", { duration: 3000 });
    } catch (e) {
      toast.error("Couldn't copy link", { description: url });
    }
  };

  const presets = templates.filter((t) => t.is_preset);
  const custom = templates.filter((t) => !t.is_preset);

  // Apply category + equipment filter
  const filterByCategory = (list) => {
    let filtered = list;
    if (categoryFilter) {
      filtered = filtered.filter((t) => t.category?.toLowerCase() === categoryFilter.toLowerCase());
    }
    if (equipFilter) {
      filtered = filtered.filter((t) => (t.equipment || "Gym").toLowerCase() === equipFilter.toLowerCase());
    }
    return filtered;
  };

  const filteredPresets = filterByCategory(presets);
  const filteredCustom = filterByCategory(custom);
  const filteredRecommended = filterByCategory(recommended);

  const categoryLabel = categoryFilter || "All";

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {categoryFilter ? `${categoryFilter} Workouts` : "Workouts"}
          </h2>
          <p className="text-sm text-[#71717A] mt-1">
            {categoryFilter
              ? `Showing ${categoryFilter.toLowerCase()} routines`
              : "Pre-built routines and your custom plans"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate?.("library")}
            className="border border-[#27272A] hover:border-[#E05A1D] text-[#A1A1AA] hover:text-[#E05A1D] font-bold uppercase tracking-wide px-4 py-2.5 text-xs flex items-center gap-2 transition-colors"
            data-testid="browse-library-btn"
          >
            <BookOpen size={14} weight="bold" /> Library
          </button>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <button
                className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-5 py-2.5 text-sm flex items-center gap-2 transition-colors active:scale-[0.98]"
                data-testid="new-workout-btn"
              >
                <Plus size={16} weight="bold" /> New Workout
              </button>
            </DialogTrigger>
          <DialogContent className="bg-[#161616] border-[#27272A] max-w-xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle
                className="text-xl font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Create Workout
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                  placeholder="Workout name"
                  data-testid="workout-name-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="template-category-select"
                  >
                    <option value="Strength">Strength</option>
                    <option value="Mass">Mass</option>
                    <option value="Cardio">Cardio</option>
                    <option value="Flexibility">Flexibility</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Equipment</label>
                  <select
                    value={equipment}
                    onChange={(e) => setEquipment(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    data-testid="template-equipment-select"
                  >
                    <option value="Gym">Gym</option>
                    <option value="Home">Home</option>
                    <option value="Bodyweight">Bodyweight</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Description</label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                    placeholder="Brief description"
                    data-testid="template-description-input"
                  />
                </div>
              </div>

              {/* Exercises */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">Exercises</label>
                  <button onClick={addExercise} className="text-xs text-[#E05A1D] font-bold uppercase flex items-center gap-1" data-testid="add-workout-exercise-btn">
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>
                <div className="space-y-3">
                  {exercises.map((ex, i) => (
                    <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-2.5 space-y-2" data-testid={`workout-exercise-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={ex.name}
                          onChange={(e) => updateExerciseField(i, "name", e.target.value)}
                          className="flex-1 bg-[#161616] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                          placeholder="Exercise name"
                          data-testid={`workout-exercise-name-${i}`}
                        />
                        <button onClick={() => removeExercise(i)} className="text-[#71717A] hover:text-red-500 transition-colors" data-testid={`workout-exercise-remove-${i}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Sets <span className="text-[#3f3f46]">(optional)</span></label>
                          <input
                            type="number"
                            min="0"
                            value={ex.numSets}
                            onChange={(e) => updateExerciseField(i, "numSets", e.target.value)}
                            className="w-full bg-[#161616] border border-[#27272A] text-white px-2 py-1.5 text-sm focus:border-[#E05A1D] outline-none"
                            placeholder="—"
                            data-testid={`workout-exercise-sets-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Reps <span className="text-[#3f3f46]">(optional)</span></label>
                          <input
                            type="number"
                            min="0"
                            value={ex.reps}
                            onChange={(e) => updateExerciseField(i, "reps", e.target.value)}
                            className="w-full bg-[#161616] border border-[#27272A] text-white px-2 py-1.5 text-sm focus:border-[#E05A1D] outline-none"
                            placeholder="—"
                            data-testid={`workout-exercise-reps-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Weight (kg) <span className="text-[#3f3f46]">(opt)</span></label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={ex.weight}
                            onChange={(e) => updateExerciseField(i, "weight", e.target.value)}
                            className="w-full bg-[#161616] border border-[#27272A] text-white px-2 py-1.5 text-sm focus:border-[#E05A1D] outline-none"
                            placeholder="—"
                            data-testid={`workout-exercise-weight-${i}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={saveTemplate}
                disabled={saving}
                className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
                data-testid="save-workout-btn"
              >
                {saving ? "Saving..." : "Save Workout"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Equipment Filter */}
      <div className="flex items-center gap-2" data-testid="equipment-filter-bar">
        {EQUIP_FILTERS.map((ef) => (
          <button
            key={ef.id || "all"}
            onClick={() => setEquipFilter(ef.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all border ${
              equipFilter === ef.id
                ? "bg-[#E05A1D] border-[#E05A1D] text-white"
                : "bg-[#0F0F0F] border-[#27272A] text-[#A1A1AA] hover:border-[#E05A1D] hover:text-white"
            }`}
            data-testid={`equip-filter-${ef.id || "all"}`}
          >
            {ef.icon && <ef.icon size={14} weight={equipFilter === ef.id ? "fill" : "regular"} />}
            {ef.label}
          </button>
        ))}
      </div>

      {/* Recommended For You */}
      <div data-testid="recommended-templates">
        <div className="flex items-center gap-2 mb-3">
          <Star size={18} weight="fill" className="text-[#E05A1D]" />
          <h3
            className="text-lg font-semibold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Recommended For You
          </h3>
          {profile && (
            <span className="text-xs text-[#71717A] ml-1">
              Based on your profile ({profile.gender}, {profile.age}y, {profile.weight}kg, {profile.height}cm)
            </span>
          )}
        </div>

        {loadingRec ? (
          <div className="flex items-center gap-3 py-12 justify-center border border-dashed border-[#27272A]" data-testid="recommended-loading">
            <Spinner size={20} className="animate-spin text-[#E05A1D]" />
            <span className="text-sm text-[#71717A]">Generating personalized templates...</span>
          </div>
        ) : recError ? (
          <div className="text-center py-8 border border-dashed border-[#27272A]">
            <p className="text-sm text-[#71717A] mb-2">Couldn't load recommendations</p>
            <button
              onClick={fetchRecommended}
              className="text-xs text-[#E05A1D] hover:text-[#F06B2E] font-bold uppercase transition-colors"
              data-testid="retry-recommended-btn"
            >
              Try Again
            </button>
          </div>
        ) : filteredRecommended.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRecommended.map((t) => (
              <RecommendedCard key={t.id} template={t} onClick={() => onOpenTemplate?.(t)} onQuickLog={onQuickLog} />
            ))}
          </div>
        ) : categoryFilter && recommended.length > 0 ? (
          <p className="text-sm text-[#71717A] py-4">No recommended {categoryFilter.toLowerCase()} workouts</p>
        ) : null}
      </div>

      {/* Presets */}
      {filteredPresets.length > 0 && (
        <div data-testid="preset-templates">
          <h3
            className="text-lg font-semibold tracking-tight uppercase text-[#A1A1AA] mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {categoryFilter ? `${categoryFilter} Workouts` : "Workouts"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPresets.map((t) => (
              <TemplateCard key={t.id} template={t} onDelete={deleteTemplate} onClick={() => onOpenTemplate?.(t)} onQuickLog={onQuickLog} lastPerformedDate={lastPerformed[(t.name || "").toLowerCase()]} />
            ))}
          </div>
        </div>
      )}

      {/* Custom */}
      <div data-testid="custom-templates">
        <h3
          className="text-lg font-semibold tracking-tight uppercase text-[#A1A1AA] mb-3"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Custom {categoryFilter || "Templates"}
        </h3>
        {filteredCustom.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-[#27272A]">
            <p className="text-[#71717A] text-sm">
              {categoryFilter
                ? `No custom ${categoryFilter.toLowerCase()} workouts yet`
                : "Create your first custom template"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCustom.map((t) => (
              <TemplateCard key={t.id} template={t} onDelete={deleteTemplate} onClick={() => onOpenTemplate?.(t)} onQuickLog={onQuickLog} onShare={shareTemplate} lastPerformedDate={lastPerformed[(t.name || "").toLowerCase()]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RecommendedCard({ template, onClick, onQuickLog }) {
  return (
    <div
      onClick={onClick}
      className="bg-[#161616] border border-[#E05A1D]/30 p-5 hover:border-[#E05A1D]/60 transition-colors ai-glow cursor-pointer"
      data-testid={`recommended-card-${template.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Star size={14} weight="fill" className="text-[#E05A1D]" />
            <h4 className="font-semibold text-white">{template.name}</h4>
          </div>
          <span className="text-xs text-[#E05A1D] font-bold uppercase">{template.category}</span>
        </div>
        {onQuickLog && (
          <button
            onClick={(e) => { e.stopPropagation(); onQuickLog(template); }}
            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E05A1D]/10 hover:bg-[#E05A1D] text-[#E05A1D] hover:text-white border border-[#E05A1D]/40 px-2 py-1 transition-all"
            data-testid={`quick-log-${template.id}`}
            title="Quick Log this workout"
          >
            <Lightning size={12} weight="fill" /> Quick Log
          </button>
        )}
      </div>
      {template.description && (
        <p className="text-xs text-[#A1A1AA] mb-3">{template.description}</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {template.exercises?.map((ex, i) => (
          <span key={i} className="text-xs text-[#71717A] bg-[#0F0F0F] border border-[#27272A] px-2 py-0.5 flex items-center gap-1">
            <Barbell size={10} weight="bold" className="text-[#E05A1D]" />
            {ex.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function TemplateCard({ template, onDelete, onClick, onQuickLog, onShare, lastPerformedDate }) {
  const equipColor = template.equipment === "Bodyweight" ? "text-[#22C55E]" : template.equipment === "Home" ? "text-[#F59E0B]" : "text-[#71717A]";

  // Compute "X days ago" badge text
  const lastAgo = (() => {
    if (!lastPerformedDate) return null;
    const ts = new Date(lastPerformedDate);
    if (isNaN(ts.getTime())) return null;
    const days = Math.floor((Date.now() - ts.getTime()) / 86400000);
    if (days < 0) return null;
    if (days === 0) return { label: "Today", stale: false };
    if (days === 1) return { label: "Yesterday", stale: false };
    if (days < 7) return { label: `${days}d ago`, stale: false };
    if (days < 30) return { label: `${days}d ago`, stale: days > 14 };
    if (days < 365) return { label: `${Math.floor(days / 30)}mo ago`, stale: true };
    return { label: `${Math.floor(days / 365)}y ago`, stale: true };
  })();

  return (
    <div onClick={onClick} className="bg-[#161616] border border-[#27272A] p-5 hover:border-[#3f3f46] transition-colors group cursor-pointer" data-testid={`template-card-${template.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0 pr-2">
          <h4 className="font-semibold text-white truncate">{template.name}</h4>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-[#E05A1D] font-bold uppercase">{template.category}</span>
            {template.equipment && (
              <span className={`text-xs font-bold uppercase ${equipColor}`}>{template.equipment}</span>
            )}
            {lastAgo && (
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${
                  lastAgo.stale
                    ? "border-[#F59E0B]/40 text-[#F59E0B] bg-[#F59E0B]/10"
                    : "border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/10"
                }`}
                title="Last performed"
                data-testid={`template-last-performed-${template.id}`}
              >
                {lastAgo.label}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onQuickLog && (
            <button
              onClick={(e) => { e.stopPropagation(); onQuickLog(template); }}
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E05A1D]/10 hover:bg-[#E05A1D] text-[#E05A1D] hover:text-white border border-[#E05A1D]/40 px-2 py-1 transition-all"
              data-testid={`quick-log-${template.id}`}
              title="Quick Log this workout"
            >
              <Lightning size={12} weight="fill" /> Quick Log
            </button>
          )}
          {!template.is_preset && onShare && (
            <button
              onClick={(e) => { e.stopPropagation(); onShare(template); }}
              className="text-[#71717A] hover:text-[#E05A1D] transition-colors opacity-0 group-hover:opacity-100"
              data-testid={`share-template-${template.id}`}
              title="Share this template"
            >
              <ShareNetwork size={16} />
            </button>
          )}
          {!template.is_preset && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(template.id); }}
              className="text-[#71717A] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
              data-testid={`delete-template-${template.id}`}
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      </div>
      {template.description && (
        <p className="text-xs text-[#A1A1AA] mb-3">{template.description}</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {template.exercises?.map((ex, i) => (
          <span key={i} className="text-xs text-[#71717A] bg-[#0F0F0F] border border-[#27272A] px-2 py-0.5 flex items-center gap-1">
            <Barbell size={10} weight="bold" className="text-[#E05A1D]" />
            {ex.name}
          </span>
        ))}
      </div>
    </div>
  );
}
